import { createAiChatHttpClient } from './httpClient';
import { projectAiChatMessagePath } from './paths';
import {
  getAiChatApiBase,
  isAiChatDummyBackend,
  useRestAiChatMessages,
} from '../config';
import { delay } from './dummyDelay';
import { extractAssistantText } from './responseParsers';

/**
 * POST chat completion for a project-scoped conversation.
 * Dummy: returns a placeholder string (no network).
 * REST (opt-in REACT_APP_AI_CHAT_USE_REST_MESSAGES=true): POST .../projects/:id/ai-chat/messages
 *
 * @param {{
 *   projectId: string,
 *   messages: Array<{ role: string, content: string }>,
 *   modulePath: string,
 * }} params
 * @returns {Promise<string>}
 */
export async function postProjectChatMessage(params) {
  const { projectId, messages, modulePath } = params;

  if (
    isAiChatDummyBackend() ||
    !getAiChatApiBase() ||
    !useRestAiChatMessages()
  ) {
    await delay(
      Number(process.env.REACT_APP_AI_CHAT_DUMMY_REPLY_LATENCY_MS) || 600,
    );
    const last = messages.length ? messages[messages.length - 1].content : '';
    const endpointHint = projectAiChatMessagePath(projectId);
    const liveHint = getAiChatApiBase()
      ? 'API base is set but this legacy path is inactive. For SSE chat use the main chat flow (POST /chatbot), or set REACT_APP_AI_CHAT_USE_REST_MESSAGES=true for REST below.'
      : 'Set REACT_APP_AI_CHAT_API_BASE (e.g. http://localhost:3001/api/v1) to use live POST /chatbot (SSE).';
    return (
      `[AI Chat — dummy response · project ${projectId}]\n\n` +
      `Module: ${modulePath || '—'}\n\n` +
      `You said:\n${last.slice(0, 500)}${last.length > 500 ? '…' : ''}\n\n` +
      `${liveHint}\n\n` +
      `_Optional REST (relative to API base): \`${endpointHint}\`_`
    );
  }

  const client = createAiChatHttpClient();
  if (!client) {
    throw new Error('AI chat HTTP client is not available.');
  }

  const { data } = await client.post(projectAiChatMessagePath(projectId), {
    messages,
    modulePath,
  });

  const text = extractAssistantText(data);
  if (!text) {
    throw new Error('The assistant returned an empty response.');
  }
  return text;
}
